import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

export default function SpreadsheetPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = `${t('spreadsheetH1')} | CSSBuy Spreadsheet`;
  }, [t]);

  const features = [
    t('spreadsheetFeature1'),
    t('spreadsheetFeature2'),
    t('spreadsheetFeature3'),
    t('spreadsheetFeature4'),
    t('spreadsheetFeature5'),
    t('spreadsheetFeature6'),
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Breadcrumb */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">{t('breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line" style={{ width: 16, height: 16 }} />
            <span className="text-foreground-700">{t('breadcrumbSpreadsheet')}</span>
          </nav>
        </div>

        <section className="py-8 md:py-12">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-6">
              {t('spreadsheetH1')}
            </h1>
            <p className="text-lg text-foreground-700 leading-relaxed mb-8 max-w-3xl">
              {t('spreadsheetIntro')}
            </p>

            {/* Features */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-10">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 bg-background-100 rounded-xl p-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-check-line text-primary-600" style={{ width: 20, height: 20 }} />
                  </div>
                  <span className="text-sm font-medium text-foreground-800">{feature}</span>
                </div>
              ))}
            </div>

            {/* Comparison Table */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">
              {t('comparisonH1')}
            </h2>
            <div className="overflow-x-auto rounded-xl border border-background-200/70 mb-10">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-4 py-3 font-semibold text-foreground-800">{t('tableFeature')}</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground-800">{t('tableCssbuy')}</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground-800">{t('tableSuperbuy')}</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground-800">{t('tableSugargoo')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-background-200/70">
                    <td className="px-4 py-3 text-foreground-700">{t('comparisonProductResearch')}</td>
                    <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                    <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                    <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                  </tr>
                  <tr className="border-t border-background-200/70 bg-background-50">
                    <td className="px-4 py-3 text-foreground-700">{t('comparisonQC')}</td>
                    <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                    <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                    <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                  </tr>
                  <tr className="border-t border-background-200/70">
                    <td className="px-4 py-3 text-foreground-700">{t('comparisonWarehouse')}</td>
                    <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                    <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                    <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                  </tr>
                  <tr className="border-t border-background-200/70 bg-background-50">
                    <td className="px-4 py-3 text-foreground-700">{t('comparisonShipping')}</td>
                    <td className="px-4 py-3 text-foreground-600">{t('tableCheck')}</td>
                    <td className="px-4 py-3 text-foreground-600">{t('tableCheck')}</td>
                    <td className="px-4 py-3 text-foreground-600">{t('tableCheck')}</td>
                  </tr>
                  <tr className="border-t border-background-200/70">
                    <td className="px-4 py-3 text-foreground-700">{t('comparisonBeginnerFriendly')}</td>
                    <td className="px-4 py-3 text-foreground-600">{t('tableCompare')}</td>
                    <td className="px-4 py-3 text-foreground-600">{t('tableCompare')}</td>
                    <td className="px-4 py-3 text-foreground-600">{t('tableCompare')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-foreground-500 mb-10">{t('comparisonNote')}</p>

            {/* CTA */}
            <div className="text-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/25 whitespace-nowrap"
              >
                {t('spreadsheetCta')}
                <i className="ri-arrow-right-line" style={{ width: 18, height: 18 }} />
              </a>
            </div>

            {/* Internal links */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="font-semibold text-foreground-950 mb-4">{t('articleRelated')}</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/cssbuy-review" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navReview')}</Link>
                <Link to="/cssbuy-shipping" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navShipping')}</Link>
                <Link to="/cssbuy-guide" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navGuide')}</Link>
                <Link to="/cssbuy-qc" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navFAQ')}</Link>
                <Link to="/cssbuy-safety" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navSafety')}</Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}